<template>
  <section
    v-if="shouldShow"
    :class="[
      'lead-follow-up-panel',
      `lead-follow-up-panel--${displayState.code || 'unknown'}`
    ]"
  >
    <header class="lead-follow-up-panel__header">
      <div class="lead-follow-up-panel__title">
        <strong>Follow-up automation</strong>
        <AppStatusBadge
          v-if="displayState.name"
          design="new"
          class="lead-follow-up-panel__state"
          :tone="statusTone(displayState.code)"
          :label="displayState.name"
        />
      </div>
      <div v-if="run || disableAction" class="lead-follow-up-panel__header-actions">
        <AppButton design="new"
          v-if="disableAction"
          class="lead-follow-up-panel__more"
          theme="empty"
          type="button"
          :disabled="actionPending"
          :aria-expanded="menuOpen ? 'true' : 'false'"
          aria-label="Follow-up actions"
          @click.native="menuOpen = !menuOpen"
        >
          ···
        </AppButton>
        <div v-if="menuOpen" class="lead-follow-up-panel__menu">
          <AppButton design="new" theme="empty" type="button" @click.native="prepareAction(disableAction)">
            Disable automatic follow-ups
          </AppButton>
        </div>
      </div>
    </header>

    <div v-if="loading && !followUp" class="lead-follow-up-panel__skeleton" aria-label="Loading follow-up automation">
      <span></span>
      <span></span>
      <span></span>
      <span></span>
      <span></span>
    </div>

    <div v-else-if="error && !followUp" class="lead-follow-up-panel__error" role="alert">
      <strong>Unable to load follow-up automation.</strong>
      <AppButton design="new" theme="empty" type="button" @click.native="load">Retry</AppButton>
    </div>

    <template v-else-if="followUp">
      <p v-if="warning" class="lead-follow-up-panel__warning" role="status">{{ warning }}</p>

      <div v-if="!run" class="lead-follow-up-panel__empty">
        <div class="lead-follow-up-panel__empty-copy">
          <div class="lead-follow-up-panel__empty-title">
            <strong>{{ displayState.code === 'disabled' ? 'Automatic follow-ups are off' : 'No active sequence' }}</strong>
            <span
              v-if="historyRuns.length"
              v-tooltip="{
                content: 'Previous runs remain available in Run history below.',
                placement: 'top',
                offset: 10,
                classes: 'tooltip--new'
              }"
              class="lead-follow-up-panel__history-info"
              role="img"
              tabindex="0"
              aria-label="Previous runs are available in Run history below"
            >
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="8" cy="8" r="6.25" />
                <path d="M8 7v4" />
                <circle cx="8" cy="4.75" r=".55" fill="currentColor" stroke="none" />
              </svg>
            </span>
          </div>
          <p v-if="!historyRuns.length">
            Start a sequence when this lead needs another contact cycle.
          </p>
        </div>
        <div class="lead-follow-up-panel__empty-actions">
          <AppButton design="new"
            v-if="startAction"
            type="button"
            :disabled="actionPending"
            @click.native="runAction(startAction)"
          >
            <span aria-hidden="true">+</span>
            {{ actionPending ? 'Starting...' : 'Start follow-up' }}
          </AppButton>
          <AppButton design="new"
            v-else-if="primaryAction"
            type="button"
            :disabled="actionPending"
            @click.native="runAction(primaryAction)"
          >
            {{ actionPending ? 'Saving...' : primaryActionLabel }}
          </AppButton>
        </div>
      </div>

      <template v-else>
        <div class="lead-follow-up-panel__summary">
          <div class="lead-follow-up-panel__summary-copy">
            <strong>{{ summaryTitle }}</strong>
            <p>{{ runDates }}</p>
            <p v-if="runOutcome">{{ runOutcome }}</p>
          </div>
          <AppButton design="new"
            v-if="primaryAction && primaryAction.code === 'resume'"
            class="lead-follow-up-panel__summary-action"
            type="button"
            :disabled="actionPending"
            @click.native="runAction(primaryAction)"
          >
            {{ actionPending && pendingActionCode === primaryAction.code ? 'Resuming...' : primaryAction.name }}
          </AppButton>
        </div>

        <div
          v-if="scheduleAdjustmentMessage(run)"
          class="lead-follow-up-panel__schedule-adjustment"
          role="status"
        >
          <strong>Schedule adjusted</strong>
          <p>{{ scheduleAdjustmentMessage(run) }}</p>
        </div>

        <div class="lead-follow-up-panel__progress-row">
          <strong>Progress</strong>
          <LeadFollowUpProgress
            :progress="progress"
            :state="displayState.code"
            :failed="displayState.code === 'failed'"
            :next-status="nextActionStatus"
          />
          <span>{{ processed }} of {{ progress.total }} follow-ups processed</span>
        </div>

        <div v-if="nextAction" class="lead-follow-up-panel__next">
          <small>Next action</small>
          <div>
            <span class="lead-follow-up-panel__calendar" aria-hidden="true">▣</span>
            <p>
              <strong>{{ nextAction.name }}</strong>
              <span v-tooltip="dateTooltip(nextAction.due_at)">
                {{ formatScheduledDate(nextAction.due_at, now) }}{{ channelSuffix(nextAction) }}
              </span>
            </p>
            <AppButton design="new"
              v-if="sendNowAction"
              class="lead-follow-up-panel__send-now"
              theme="empty"
              type="button"
              :disabled="actionPending"
              @click.native="prepareAction(sendNowAction)"
            >
              Send now
            </AppButton>
          </div>
        </div>

        <div
          v-if="primaryAction && primaryAction.code !== 'resume' && !preparedAction"
          class="lead-follow-up-panel__controls"
        >
          <AppButton design="new"
            type="button"
            :disabled="actionPending"
            @click.native="primaryAction.code === 'delay' ? prepareAction(primaryAction) : runAction(primaryAction)"
          >
            {{ actionPending && pendingActionCode === primaryAction.code ? 'Saving...' : primaryAction.name }}
          </AppButton>
        </div>

        <form
          v-if="preparedAction && preparedAction.code === 'delay'"
          class="lead-follow-up-panel__form"
          @submit.prevent="submitDelay"
        >
          <strong>Delay follow-up schedule</strong>
          <label>
            <span>Delay until <i class="lead-follow-up-panel__required" aria-hidden="true">*</i></span>
            <AppDatePicker design="new"
              v-model="delayForm.until"
              type="datetime"
              format="DD.MM.YYYY HH:mm"
              value-type="YYYY-MM-DDTHH:mm"
              :disabled-date="disabledDelayDate"
              :error="Boolean(fieldErrors.until)"
            />
            <small v-if="fieldErrors.until">{{ fieldErrors.until }}</small>
          </label>
          <label>
            <span>Reason <i class="lead-follow-up-panel__required" aria-hidden="true">*</i></span>
            <AppTextarea design="new"
              v-model="delayForm.reason"
              class="lead-follow-up-panel__textarea"
              maxlength="255"
              :error="Boolean(fieldErrors.reason)"
            />
            <small v-if="fieldErrors.reason">{{ fieldErrors.reason }}</small>
          </label>
          <p v-if="actionError" role="alert">{{ actionError }}</p>
          <div>
            <AppButton design="new" theme="empty" type="button" :disabled="actionPending" @click.native="cancelPreparedAction">
              Cancel
            </AppButton>
            <AppButton design="new" type="submit" :disabled="actionPending">
              {{ actionPending ? 'Delaying...' : 'Delay' }}
            </AppButton>
          </div>
        </form>

        <div
          v-else-if="preparedAction && preparedAction.code === 'disable'"
          class="lead-follow-up-panel__confirmation"
        >
          <strong>Disable automatic follow-ups?</strong>
          <p>The active run and all remaining planned steps will be cancelled. Enabling later will not restore this run.</p>
          <label>
            <span>Reason <i>optional</i></span>
            <AppTextarea design="new"
              v-model="disableForm.reason"
              class="lead-follow-up-panel__textarea"
              :error="Boolean(fieldErrors.reason)"
            />
            <small v-if="fieldErrors.reason">{{ fieldErrors.reason }}</small>
          </label>
          <p v-if="actionError" class="lead-follow-up-panel__action-error" role="alert">{{ actionError }}</p>
          <div>
            <AppButton design="new" theme="empty" type="button" :disabled="actionPending" @click.native="cancelPreparedAction">
              Cancel
            </AppButton>
            <AppButton design="new" type="button" :disabled="actionPending" @click.native="submitDisable">
              {{ actionPending ? 'Disabling...' : 'Disable' }}
            </AppButton>
          </div>
        </div>

        <div
          v-else-if="preparedAction && preparedAction.code === 'send_now'"
          class="lead-follow-up-panel__confirmation"
        >
          <strong>Send {{ nextAction.name }} now?</strong>
          <p>
            {{ channelName(nextAction) || 'Configured channel' }} · scheduled
            <span v-tooltip="dateTooltip(nextAction.due_at)">{{ formatScheduledDate(nextAction.due_at, now) }}</span>
          </p>
          <p v-if="actionError" class="lead-follow-up-panel__action-error" role="alert">{{ actionError }}</p>
          <div>
            <AppButton design="new" theme="empty" type="button" :disabled="actionPending" @click.native="cancelPreparedAction">
              Cancel
            </AppButton>
            <AppButton design="new" type="button" :disabled="actionPending" @click.native="runAction(preparedAction)">
              {{ actionPending ? 'Sending...' : 'Send now' }}
            </AppButton>
          </div>
        </div>

        <div v-if="actions.length" class="lead-follow-up-panel__schedule">
          <article
            v-for="action in actions"
            :key="action.id"
            :class="[
              'lead-follow-up-panel__action',
              `lead-follow-up-panel__action--${statusCode(action)}`,
              { 'lead-follow-up-panel__action--flow': action.action_type === 'flow_event' }
            ]"
          >
            <span class="lead-follow-up-panel__status-icon" aria-hidden="true">
              {{ action.action_type === 'flow_event' ? '→' : statusIcon(action) }}
            </span>
            <div>
              <div class="lead-follow-up-panel__action-heading">
                <strong>{{ action.action_type === 'flow_event' ? `If no reply: ${action.name}` : action.name }}</strong>
                <AppStatusBadge
                  v-if="action.action_type === 'flow_event' || !hasMultipleDeliveries(action)"
                  design="new"
                  :tone="statusTone(statusCode(action))"
                  :label="statusName(action)"
                />
                <div v-else class="lead-follow-up-panel__delivery-statuses">
                  <AppStatusBadge
                    v-for="delivery in actionDeliveries(action)"
                    :key="delivery.id"
                    design="new"
                    :tone="statusTone(statusCode(delivery))"
                    :label="deliveryStatusLabel(delivery)"
                  />
                </div>
              </div>
              <p
                v-tooltip="dateTooltip(action.due_at)"
                class="lead-follow-up-panel__action-description"
              >
                {{ actionDescription(action) }}
              </p>
              <p v-if="action.sent_at" v-tooltip="dateTooltip(action.sent_at)">
                Sent {{ formatRelativeDate(action.sent_at, now) }}
              </p>
              <p v-if="action.is_manual && action.triggered_by">
                Manual send by {{ action.triggered_by.name }}
              </p>
              <p v-if="action.error && !hasMultipleDeliveries(action)" class="lead-follow-up-panel__row-error">
                {{ action.error.message || action.error }}
              </p>
              <p
                v-for="delivery in deliveryErrors(action)"
                :key="`delivery-error-${delivery.id}`"
                class="lead-follow-up-panel__row-error"
              >
                <strong>{{ channelName(delivery) || 'Delivery' }}:</strong>
                {{ delivery.error.message || delivery.error }}
              </p>
              <p
                v-for="delivery in fallbackDeliveries(action)"
                :key="`delivery-language-${delivery.id}`"
              >
                {{ channelName(delivery) || 'Delivery' }}: requested {{ delivery.language_selection.requested }}, English template selected ({{ delivery.language_selection.selected }}).
              </p>
              <p v-if="action.cancelled_at" v-tooltip="dateTooltip(action.cancelled_at)">
                Cancelled {{ formatRelativeDate(action.cancelled_at, now) }}{{ cancellationReason(action) }}
              </p>
              <AppButton design="new"
                v-if="action.template"
                class="lead-follow-up-panel__preview-button"
                theme="empty"
                type="button"
                @click.native="toggleTemplate(action.id)"
              >
                {{ expandedTemplates[action.id] ? 'Hide template' : 'Preview template' }}
              </AppButton>
              <div v-if="expandedTemplates[action.id]" class="lead-follow-up-panel__template">
                <strong>{{ action.template.title || action.template.name || 'Message template' }}</strong>
                <span v-if="templateMeta(action.template)">{{ templateMeta(action.template) }}</span>
                <p>{{ templateText(action.template) }}</p>
              </div>
            </div>
          </article>
        </div>
      </template>

      <div v-if="historyRuns.length" class="lead-follow-up-panel__history">
        <div class="lead-follow-up-panel__history-heading">
          <strong>Run history</strong>
          <span>{{ historyRuns.length }} {{ historyRuns.length === 1 ? 'run' : 'runs' }}</span>
        </div>
        <details v-for="historyRun in historyRuns" :key="historyRun.id">
          <summary>
            <span>
              <strong>{{ runTitle(historyRun) }}</strong>
              <small>{{ runDatesFor(historyRun) }}</small>
            </span>
            <AppStatusBadge
              design="new"
              class="lead-follow-up-panel__history-status"
              :tone="statusTone(runStatusCode(historyRun))"
              :label="runStatusName(historyRun)"
            />
          </summary>
          <div class="lead-follow-up-panel__history-body">
            <div class="lead-follow-up-panel__history-summary">
              <p v-if="runOutcomeFor(historyRun)">{{ runOutcomeFor(historyRun) }}</p>
              <p v-if="scheduleAdjustmentMessage(historyRun)">
                {{ scheduleAdjustmentMessage(historyRun) }}
              </p>
              <p>{{ processedCount(historyRun.progress) }} of {{ historyRun.progress.total }} follow-ups processed</p>
            </div>
            <div class="lead-follow-up-panel__history-actions">
              <div v-for="action in sortedRunActions(historyRun)" :key="action.id">
                <span
                  :class="[
                    'lead-follow-up-panel__history-action-icon',
                    action.action_type === 'flow_event'
                      ? 'lead-follow-up-panel__history-action-icon--flow'
                      : `lead-follow-up-panel__history-action-icon--${statusCode(action)}`
                  ]"
                  aria-hidden="true"
                >
                  {{ action.action_type === 'flow_event' ? '→' : statusIcon(action) }}
                </span>
                <p>
                  <strong>{{ action.action_type === 'flow_event' ? `If no reply: ${action.name}` : action.name }}</strong>
                  <small>{{ statusName(action) }} · {{ actionDescription(action) }}</small>
                </p>
              </div>
            </div>
          </div>
        </details>
        <p v-if="followUp.run_history_truncated" class="lead-follow-up-panel__history-truncated">
          Showing the 10 most recent runs.
        </p>
      </div>
    </template>
  </section>
</template>

<script>
import {
  getLeadFlowFollowUp,
  postLeadFlowFollowUpAction,
  postLeadFlowFollowUpSendNow
} from '@/http'
import LeadFollowUpProgress from '@/views/crm/task-tracker/LeadFollowUpProgress.vue'
import { processedCount } from '@/views/crm/task-tracker/leadFollowUpProgress'
import {
  isFollowUpDelayDateDisabled,
  validateFollowUpDelay
} from '@/views/crm/task-tracker/leadFollowUpDelay'
import {
  formatExactDate,
  formatRelativeDate,
  formatScheduledDate,
  formatShortDate
} from '@/views/crm/task-tracker/leadFlowDate'

export default {
  name: 'LeadFollowUpAutomationPanel',
  components: {
    LeadFollowUpProgress
  },
  props: {
    leadId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      followUp: null,
      loading: false,
      error: null,
      requestGeneration: 0,
      pendingActionCode: '',
      actionError: '',
      fieldErrors: {},
      warning: '',
      preparedAction: null,
      menuOpen: false,
      delayForm: {
        until: '',
        reason: ''
      },
      disableForm: {
        reason: ''
      },
      expandedTemplates: {},
      now: Date.now(),
      minuteTimer: null
    }
  },
  computed: {
    shouldShow() {
      if (this.loading || this.error) return true
      if (!this.followUp) return false
      return this.followUp.available !== false || Boolean(
        this.followUp.active_run || this.followUp.latest_run || this.historyRuns.length
      )
    },
    displayState() {
      return this.followUp && this.followUp.display_state ? this.followUp.display_state : {}
    },
    run() {
      if (!this.followUp) return null
      return this.followUp.active_run || null
    },
    historyRuns() {
      if (!this.followUp) return []
      if (Array.isArray(this.followUp.run_history)) return this.followUp.run_history
      return this.followUp.latest_run ? [this.followUp.latest_run] : []
    },
    progress() {
      return this.run && this.run.progress ? this.run.progress : { sent: 0, total: 0 }
    },
    processed() {
      return processedCount(this.progress)
    },
    nextAction() {
      return this.run && this.run.next_action ? this.run.next_action : null
    },
    nextActionStatus() {
      if (!this.nextAction) return ''
      const status = this.nextAction.status
      return typeof status === 'string' ? status : (status && status.code) || ''
    },
    actions() {
      if (!this.run || !Array.isArray(this.run.actions)) return []
      return [...this.run.actions].sort((left, right) => left.position - right.position)
    },
    availableActions() {
      if (!this.followUp || !Array.isArray(this.followUp.available_actions)) return []
      return this.followUp.available_actions
    },
    startAction() {
      return this.findAction('start')
    },
    disableAction() {
      return this.findAction('disable')
    },
    sendNowAction() {
      const action = this.findAction('send_now')
      if (!action || !this.nextAction) return null
      return Number(action.action_id) === Number(this.nextAction.id) ? action : null
    },
    primaryAction() {
      return {
        active: this.findAction('delay'),
        failed: this.findAction('delay'),
        paused: this.findAction('resume'),
        disabled: this.findAction('enable')
      }[this.displayState.code] || null
    },
    primaryActionLabel() {
      if (!this.primaryAction) return ''
      return this.primaryAction.code === 'enable' ? 'Enable automatic follow-ups' : this.primaryAction.name
    },
    actionPending() {
      return Boolean(this.pendingActionCode)
    },
    summaryTitle() {
      const stage = this.followUp.stage && this.followUp.stage.name
      const policy = this.followUp.policy && this.followUp.policy.name
      return [stage, policy].filter(Boolean).join(' · ') || 'Follow-up sequence'
    },
    runDates() {
      if (!this.run) return ''
      const parts = []
      if (this.run.started_at) parts.push(`Started ${formatShortDate(this.run.started_at)}`)
      if (this.run.anchor_at) parts.push(`anchored ${formatShortDate(this.run.anchor_at)}`)
      return parts.join(' · ')
    },
    runOutcome() {
      if (!this.followUp || this.followUp.active_run || !this.run) return ''
      const reason = this.run.display_reason || this.run.cancellation_reason || this.run.cancelled_reason
      const reasonName = reason && (reason.name || reason.message || reason)
      if (reasonName) return reasonName
      const finalOutcome = this.followUp.policy && this.followUp.policy.final_outcome
      return this.displayState.code === 'completed' && finalOutcome ? `Completed · ${finalOutcome}` : ''
    }
  },
  watch: {
    leadId: {
      immediate: true,
      handler() {
        this.reset()
        this.load()
      }
    }
  },
  mounted() {
    this.minuteTimer = setInterval(() => {
      this.now = Date.now()
    }, 60000)
  },
  beforeDestroy() {
    clearInterval(this.minuteTimer)
    this.requestGeneration += 1
  },
  methods: {
    processedCount,
    formatRelativeDate,
    formatScheduledDate,
    sortedRunActions(run) {
      if (!run || !Array.isArray(run.actions)) return []
      return [...run.actions].sort((left, right) => left.position - right.position)
    },
    runStatusCode(run) {
      const status = run && run.status
      return typeof status === 'string' ? status : (status && status.code) || 'cancelled'
    },
    runStatusName(run) {
      const status = run && run.status
      if (status && status.name) return status.name
      const code = this.runStatusCode(run)
      return code.charAt(0).toUpperCase() + code.slice(1)
    },
    runTitle(run) {
      const stage = run && run.stage && run.stage.name
      const policy = run && run.policy && run.policy.name
      return [stage, policy].filter(Boolean).join(' · ') || 'Follow-up sequence'
    },
    runDatesFor(run) {
      if (!run) return ''
      const parts = []
      if (run.started_at) parts.push(`Started ${formatShortDate(run.started_at)}`)
      if (run.anchor_at) parts.push(`anchored ${formatShortDate(run.anchor_at)}`)
      return parts.join(' · ')
    },
    runOutcomeFor(run) {
      if (!run) return ''
      const reason = run.display_reason || run.cancellation_reason || run.cancelled_reason
      const reasonName = reason && (reason.name || reason.message || reason)
      if (reasonName) return reasonName
      const finalOutcome = run.policy && run.policy.final_outcome
      return this.runStatusCode(run) === 'completed' && finalOutcome ? `Completed · ${finalOutcome}` : ''
    },
    scheduleAdjustmentMessage(run) {
      const adjustment = run && run.schedule_adjustment
      if (!adjustment || adjustment.code !== 'minimum_planning_lead_time_applied') return ''
      let firstDelivery = ''
      if (adjustment.scheduled_first_due_at) {
        firstDelivery = ` The first remaining delivery was scheduled for ${formatShortDate(adjustment.scheduled_first_due_at)}.`
      }
      return `The original schedule was already due or too close, so the whole sequence was moved forward with its intervals preserved.${firstDelivery}`
    },
    async load(options = {}) {
      const leadId = this.leadId
      const generation = this.requestGeneration + 1
      this.requestGeneration = generation
      this.loading = true
      this.error = null
      try {
        const { data } = await getLeadFlowFollowUp(leadId)
        if (generation !== this.requestGeneration || leadId !== this.leadId) return null
        this.followUp = data || null
        if (!options.keepWarning) this.warning = ''
        return data || null
      } catch (error) {
        if (generation === this.requestGeneration && leadId === this.leadId) this.error = error
        return null
      } finally {
        if (generation === this.requestGeneration && leadId === this.leadId) this.loading = false
      }
    },
    refreshAfterInbound() {
      return this.load()
    },
    findAction(code) {
      return this.availableActions.find(action => action.code === code) || null
    },
    prepareAction(action) {
      if (!action || this.actionPending) return
      this.preparedAction = action
      this.menuOpen = false
      this.actionError = ''
      this.fieldErrors = {}
    },
    cancelPreparedAction() {
      if (this.actionPending) return
      this.preparedAction = null
      this.actionError = ''
      this.fieldErrors = {}
    },
    disabledDelayDate(value) {
      return isFollowUpDelayDateDisabled(value, Date.now())
    },
    submitDelay() {
      const errors = validateFollowUpDelay(this.delayForm, Date.now())
      if (Object.keys(errors).length) {
        this.fieldErrors = errors
        return
      }
      const payload = {
        until: new Date(this.delayForm.until).toISOString(),
        reason: this.delayForm.reason.trim()
      }
      this.runAction(this.preparedAction, payload)
    },
    submitDisable() {
      this.runAction(this.preparedAction, { reason: this.disableForm.reason.trim() })
    },
    async runAction(action, payload = {}) {
      if (!action || this.actionPending) return
      const leadId = this.leadId
      this.pendingActionCode = action.code
      this.actionError = ''
      this.fieldErrors = {}
      this.warning = ''
      try {
        let response = null
        if (action.code === 'send_now') {
          response = await postLeadFlowFollowUpSendNow(leadId, action.action_id)
        } else {
          response = await postLeadFlowFollowUpAction(leadId, action.code, payload)
        }
        if (leadId !== this.leadId) return
        const data = response.data || {}
        this.followUp = data.follow_up || this.followUp
        this.preparedAction = null
        if (action.code === 'delay') this.delayForm = { until: '', reason: '' }
        this.$emit('board-reconcile', data)
        this.$notify({ type: 'success', title: action.name || 'Follow-up automation updated' })
      } catch (error) {
        if (leadId !== this.leadId) return
        const response = error.response || {}
        if (response.status === 409) {
          this.preparedAction = null
          this.followUp = null
          const refreshed = await this.load({ keepWarning: true })
          if (refreshed) {
            this.warning = 'Follow-up automation changed in another session. The latest state is shown.'
            if (response.data && response.data.code === 'follow_up_delay_run_missing') {
              this.warning = 'The active follow-up sequence changed before the delay was saved. Its current state is shown.'
            }
          }
        } else {
          this.applyActionError(response.data || {})
        }
      } finally {
        if (leadId === this.leadId) this.pendingActionCode = ''
      }
    },
    applyActionError(data) {
      const fields = data.errors || data
      const fieldErrors = {}
      const errorFields = ['until', 'reason']
      errorFields.forEach(field => {
        const value = fields[field]
        if (value) fieldErrors[field] = Array.isArray(value) ? value.join(' ') : value
      })
      this.fieldErrors = fieldErrors
      this.actionError = data.message || data.detail || data.details || (Object.keys(fieldErrors).length ? '' : 'Unable to update follow-up automation.')
    },
    dateTooltip(value) {
      return { content: formatExactDate(value), placement: 'top', offset: 8 }
    },
    channelName(action) {
      return action.channel && action.channel.name ? action.channel.name : ''
    },
    channelSuffix(action) {
      const channel = this.channelName(action)
      return channel ? ` · ${channel}` : ''
    },
    actionDeliveries(action) {
      return action && Array.isArray(action.deliveries) ? action.deliveries : []
    },
    hasMultipleDeliveries(action) {
      return this.actionDeliveries(action).length > 1
    },
    deliveryStatusLabel(delivery) {
      const channel = this.channelName(delivery) || 'Delivery'
      return `${channel} · ${this.statusName(delivery)}`
    },
    deliveryErrors(action) {
      if (!this.hasMultipleDeliveries(action)) return []
      return this.actionDeliveries(action).filter(delivery => delivery.error)
    },
    fallbackDeliveries(action) {
      return this.actionDeliveries(action).filter(delivery => delivery.language_selection && delivery.language_selection.fallback)
    },
    statusCode(action) {
      const status = action.status
      return typeof status === 'string' ? status : (status && status.code) || 'planned'
    },
    statusName(action) {
      const status = action.status
      if (status && status.name) return status.name
      const code = this.statusCode(action)
      return code.charAt(0).toUpperCase() + code.slice(1)
    },
    statusTone(code) {
      if (['active', 'completed', 'sent', 'ready', 'not_started'].includes(code)) return 'green'
      if (['planned', 'sending', 'in_progress'].includes(code)) return 'blue'
      if (['paused', 'attention'].includes(code)) return 'amber'
      if (['failed', 'cancelled'].includes(code)) return 'red'
      return 'neutral'
    },
    statusIcon(action) {
      return {
        sent: '✓',
        sending: '↻',
        failed: '!',
        unknown: '?',
        cancelled: '×'
      }[this.statusCode(action)] || '○'
    },
    actionDescription(action) {
      if (action.action_type === 'flow_event') return `Scheduled ${formatScheduledDate(action.due_at, this.now)}`
      const channel = this.hasMultipleDeliveries(action) ? '' : this.channelName(action)
      return `${channel ? `${channel} · ` : ''}Scheduled ${formatScheduledDate(action.due_at, this.now)}`
    },
    cancellationReason(action) {
      const reason = action.cancellation_reason || action.cancelled_reason
      const name = reason && (reason.name || reason.message || reason)
      return name ? ` · ${name}` : ''
    },
    toggleTemplate(actionId) {
      this.$set(this.expandedTemplates, actionId, !this.expandedTemplates[actionId])
    },
    templateMeta(template) {
      return [template.name && template.name !== template.title ? template.name : '', template.language]
        .filter(Boolean)
        .join(' · ')
    },
    templateText(template) {
      return template.preview || template.body || template.text || template.message || template.content || 'Template text is not available in this response.'
    },
    reset() {
      this.requestGeneration += 1
      this.followUp = null
      this.loading = false
      this.error = null
      this.pendingActionCode = ''
      this.actionError = ''
      this.fieldErrors = {}
      this.warning = ''
      this.preparedAction = null
      this.menuOpen = false
      this.delayForm = { until: '', reason: '' }
      this.disableForm = { reason: '' }
      this.expandedTemplates = {}
    }
  }
}
</script>

<style scoped>
.lead-follow-up-panel {
  position: relative;
  display: grid;
  min-width: 0;
  gap: 9px;
  padding: 15px;
  border: 1px solid #dfe4dd;
  border-radius: 7px;
  background: #fff;
  color: #344748;
}

.lead-follow-up-panel__header,
.lead-follow-up-panel__title,
.lead-follow-up-panel__header-actions,
.lead-follow-up-panel__action-heading,
.lead-follow-up-panel__controls,
.lead-follow-up-panel__confirmation > div,
.lead-follow-up-panel__form > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.lead-follow-up-panel__title {
  justify-content: flex-start;
  min-width: 0;
}

.lead-follow-up-panel__title > strong {
  color: #657574;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.lead-follow-up-panel__header-actions {
  position: relative;
  justify-content: flex-end;
}

.lead-follow-up-panel__title > span,
.lead-follow-up-panel__action-heading > span {
  padding: 3px 7px;
  border-radius: 10px;
  background: #eef2ed;
  color: #657472;
  font-size: 10px;
}

.lead-follow-up-panel__header-actions > b {
  font-size: 11px;
}

.lead-follow-up-panel__state--active,
.lead-follow-up-panel__state--not_started,
.lead-follow-up-panel__state--completed {
  background: #edf5e8 !important;
  color: #4b8428 !important;
}

.lead-follow-up-panel__state--paused {
  background: #fff4dc !important;
  color: #936d28 !important;
}

.lead-follow-up-panel__state--failed {
  background: #fff0ed !important;
  color: #ad5149 !important;
}

.lead-follow-up-panel__action--planned .lead-follow-up-panel__action-heading > span,
.lead-follow-up-panel__action--sending .lead-follow-up-panel__action-heading > span {
  background: #edf5fb;
  color: #3f7899;
}

.lead-follow-up-panel__action--sent .lead-follow-up-panel__action-heading > span,
.lead-follow-up-panel__action--completed .lead-follow-up-panel__action-heading > span {
  background: #edf5e8;
  color: #4b8428;
}

.lead-follow-up-panel__action--skipped .lead-follow-up-panel__action-heading > span {
  background: #eef2ed;
  color: #73807e;
}

.lead-follow-up-panel__action--cancelled .lead-follow-up-panel__action-heading > span,
.lead-follow-up-panel__action--failed .lead-follow-up-panel__action-heading > span {
  background: #fff0ed;
  color: #ad5149;
}

.lead-follow-up-panel__action--unknown .lead-follow-up-panel__action-heading > span {
  background: #fff4dc;
  color: #936d28;
}

.lead-follow-up-panel__more {
  min-width: 32px;
  min-height: 30px;
  width: 32px;
  height: 30px;
  padding: 0;
  border: 1px solid #d8dfd5;
  border-radius: 5px;
  background: #fff;
  color: #667574;
  cursor: pointer;
}

.lead-follow-up-panel__menu {
  position: absolute;
  z-index: 4;
  top: 35px;
  right: 0;
  width: 220px;
  padding: 5px;
  border: 1px solid #dfe4dd;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 7px 20px rgba(29, 48, 43, .12);
}

.lead-follow-up-panel__menu button {
  justify-content: flex-start;
  min-width: 0;
  min-height: 0;
  width: 100%;
  padding: 9px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #ad5149;
  text-align: left;
  cursor: pointer;
}

.lead-follow-up-panel__summary > strong {
  font-size: 13px;
  line-height: 18px;
}

.lead-follow-up-panel__summary {
  padding: 9px 12px;
  border-radius: 7px;
  background: #f5f7f5;
}

.lead-follow-up-panel--active .lead-follow-up-panel__summary,
.lead-follow-up-panel--completed .lead-follow-up-panel__summary {
  background: #f3f8f0;
}

.lead-follow-up-panel--paused .lead-follow-up-panel__summary {
  background: #fff8e9;
}

.lead-follow-up-panel--failed .lead-follow-up-panel__summary {
  background: #fff3f0;
}

.lead-follow-up-panel__schedule-adjustment {
  padding: 9px 12px;
  border-left: 3px solid #c89322;
  border-radius: 6px;
  background: #fff8e9;
  color: #596a69;
}

.lead-follow-up-panel__schedule-adjustment strong {
  color: #6f5318;
  font-size: 11px;
  line-height: 14px;
}

.lead-follow-up-panel__schedule-adjustment p {
  margin: 3px 0 0;
  font-size: 11px;
  line-height: 15px;
}

.lead-follow-up-panel__summary > p,
.lead-follow-up-panel__action p,
.lead-follow-up-panel__empty p,
.lead-follow-up-panel__confirmation p {
  margin: 4px 0 0;
  color: #71807f;
  font-size: 11px;
  line-height: 14px;
}

.lead-follow-up-panel__progress-row {
  display: grid;
  grid-template-columns: auto minmax(110px, 1fr) auto;
  align-items: center;
  gap: 11px;
  min-height: 20px;
}

.lead-follow-up-panel__progress-row > strong,
.lead-follow-up-panel__progress-row > span {
  color: #596a69;
  font-size: 10px;
}

.lead-follow-up-panel__progress-row > strong {
  font-weight: 700;
}

.lead-follow-up-panel__next {
  padding: 12px;
  border-radius: 6px;
  background: #f2f7ee;
}

.lead-follow-up-panel__next > small {
  color: #758574;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: .09em;
  text-transform: uppercase;
}

.lead-follow-up-panel__next > div {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;
  margin-top: 8px;
}

.lead-follow-up-panel__next p {
  display: grid;
  gap: 3px;
  margin: 0;
}

.lead-follow-up-panel__next p span {
  color: #75817f;
  font-size: 10px;
}

.lead-follow-up-panel__next button,
.lead-follow-up-panel__controls button,
.lead-follow-up-panel__empty button,
.lead-follow-up-panel__confirmation button,
.lead-follow-up-panel__form button {
  min-width: 0;
  min-height: 32px;
  padding: 0 12px;
  border: 1px solid #77a63a;
  border-radius: 5px;
  background: #fff;
  color: #4f8425;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
}

.lead-follow-up-panel button:disabled {
  cursor: wait;
  opacity: .55;
}

.lead-follow-up-panel__controls {
  justify-content: flex-end;
}

.lead-follow-up-panel__controls button,
.lead-follow-up-panel__empty button,
.lead-follow-up-panel__form button:last-child {
  background: #69a433;
  color: #fff;
}

.lead-follow-up-panel__empty {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px;
  border: 1px solid #e6eaf0;
  border-radius: 10px;
  background: #f7f9fc;
}

.lead-follow-up-panel__empty-copy {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.lead-follow-up-panel__empty-title {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.lead-follow-up-panel__empty-title > strong {
  font-size: 12px;
}

.lead-follow-up-panel__history-info {
  display: inline-flex;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  align-items: center;
  justify-content: center;
  color: #7a8988;
  cursor: pointer;
}

.lead-follow-up-panel__history-info svg {
  width: 14px;
  height: 14px;
  stroke: currentColor;
  stroke-width: 1.25;
  stroke-linecap: round;
}

.lead-follow-up-panel__history-info:focus-visible {
  border-radius: 50%;
  outline: 2px solid rgba(37, 99, 235, .24);
  outline-offset: 2px;
}

.lead-follow-up-panel__empty-copy > p {
  margin: 0;
}

.lead-follow-up-panel__empty-actions {
  flex: 0 0 auto;
}

.lead-follow-up-panel__history {
  display: grid;
  gap: 7px;
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #e4e8e2;
}

.lead-follow-up-panel__history-heading,
.lead-follow-up-panel__history summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.lead-follow-up-panel__history-heading > strong {
  color: #657574;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.lead-follow-up-panel__history-heading > span {
  color: #87918f;
  font-size: 10px;
}

.lead-follow-up-panel__history details {
  overflow: hidden;
  border: 1px solid #e1e6df;
  border-radius: 6px;
  background: #fbfcfa;
}

.lead-follow-up-panel__history summary {
  padding: 9px 10px;
  cursor: pointer;
  list-style-position: inside;
}

.lead-follow-up-panel__history summary > span:first-child {
  display: inline-grid;
  min-width: 0;
  flex: 1 1 auto;
  gap: 2px;
}

.lead-follow-up-panel__history summary strong {
  overflow: hidden;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-follow-up-panel__history summary small {
  color: #7c8987;
  font-size: 11px;
  line-height: 14px;
}

.lead-follow-up-panel__history-actions small {
  color: #7c8987;
  font-size: 9px;
}

.lead-follow-up-panel__history summary > span:last-child {
  flex: 0 0 auto;
  padding: 3px 7px;
  border-radius: 10px;
  background: #eef2ed;
  color: #657472;
  font-size: 9px;
}

.lead-follow-up-panel__history-status--completed {
  background: #edf5e8 !important;
  color: #4b8428 !important;
}

.lead-follow-up-panel__history-status--active,
.lead-follow-up-panel__history-status--not_started {
  background: #edf5fb !important;
  color: #3f7899 !important;
}

.lead-follow-up-panel__history-status--paused {
  background: #fff4dc !important;
  color: #936d28 !important;
}

.lead-follow-up-panel__history-status--cancelled,
.lead-follow-up-panel__history-status--failed {
  background: #fff0ed !important;
  color: #ad5149 !important;
}

.lead-follow-up-panel__history-body {
  padding: 0 10px 10px;
  border-top: 1px solid #e7ebe5;
}

.lead-follow-up-panel__history-body > p,
.lead-follow-up-panel__history-truncated {
  margin: 7px 0 0;
  color: #71807f;
  font-size: 10px;
}

.lead-follow-up-panel__history-actions {
  display: grid;
  gap: 6px;
  margin-top: 9px;
}

.lead-follow-up-panel__history-actions > div {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 7px;
}

.lead-follow-up-panel__history-action-icon {
  display: inline-flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  color: #71807f;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
}

.lead-follow-up-panel__history-action-icon--flow,
.lead-follow-up-panel__history-action-icon--planned,
.lead-follow-up-panel__history-action-icon--sending {
  color: #1769e0;
}

.lead-follow-up-panel__history-action-icon--sent,
.lead-follow-up-panel__history-action-icon--completed {
  color: #1b8f65;
}

.lead-follow-up-panel__history-action-icon--cancelled,
.lead-follow-up-panel__history-action-icon--failed {
  color: #c53b4b;
}

.lead-follow-up-panel__history-action-icon--unknown {
  color: #a96a00;
}

.lead-follow-up-panel__history-actions p {
  display: grid;
  gap: 1px;
  margin: 0;
}

.lead-follow-up-panel__history-actions strong {
  font-size: 10px;
}

.lead-follow-up-panel__form,
.lead-follow-up-panel__confirmation {
  display: grid;
  gap: 11px;
  padding: 13px;
  border: 1px solid #e3d6b6;
  border-radius: 6px;
  background: #fffaf0;
}

.lead-follow-up-panel__confirmation button {
  transition: background-color .15s ease, border-color .15s ease;
}

.lead-follow-up-panel__confirmation button:first-child:hover:not(:disabled),
.lead-follow-up-panel__confirmation button:first-child:focus-visible:not(:disabled) {
  border-color: #b7c5b0;
  background: #f3f6f1;
}

.lead-follow-up-panel__confirmation button:last-child:hover:not(:disabled),
.lead-follow-up-panel__confirmation button:last-child:focus-visible:not(:disabled) {
  border-color: #65952f;
  background: rgba(105, 164, 51, .13);
}

.lead-follow-up-panel__form label,
.lead-follow-up-panel__confirmation label {
  display: grid;
  gap: 5px;
}

.lead-follow-up-panel__form label span,
.lead-follow-up-panel__confirmation label span {
  color: #667675;
  font-size: 10px;
  font-weight: 700;
}

.lead-follow-up-panel__form i,
.lead-follow-up-panel__confirmation i {
  color: #929c9a;
  font-style: normal;
  font-weight: 500;
}

.lead-follow-up-panel__form .lead-follow-up-panel__required {
  color: #c53b4b;
  font-weight: 700;
}

.lead-follow-up-panel__textarea {
  min-height: 50px;
  padding: 8px 9px;
  border: 1px solid #d7ded5;
  border-radius: 5px;
  background: #fff;
  color: #344748;
  font: inherit;
  font-size: 11px;
}

.lead-follow-up-panel__form small,
.lead-follow-up-panel__confirmation small,
.lead-follow-up-panel__action-error,
.lead-follow-up-panel__row-error {
  color: #b85049 !important;
  font-size: 9px;
}

.lead-follow-up-panel__schedule {
  display: grid;
  overflow: hidden;
  border: 1px solid #e1e6df;
  border-radius: 7px;
  background: #fff;
}

.lead-follow-up-panel__action {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 9px;
  padding: 6px 10px;
  border-top: 1px solid #e7ebe5;
}

.lead-follow-up-panel__action:first-child {
  border-top: 0;
}

.lead-follow-up-panel__action--flow {
  margin-top: 0;
  border-top-color: #cad9c1;
  background: #fbfcfa;
}

.lead-follow-up-panel__status-icon {
  align-self: start;
  margin-top: 1px;
  color: #66a433;
  font-size: 16px;
  font-weight: 700;
}

.lead-follow-up-panel__action--failed .lead-follow-up-panel__status-icon,
.lead-follow-up-panel__action--cancelled .lead-follow-up-panel__status-icon {
  color: #b85049;
}

.lead-follow-up-panel__action--flow .lead-follow-up-panel__status-icon,
.lead-follow-up-panel__action--planned .lead-follow-up-panel__status-icon,
.lead-follow-up-panel__action--sending .lead-follow-up-panel__status-icon {
  color: #1769e0;
}

.lead-follow-up-panel__action--unknown .lead-follow-up-panel__status-icon {
  color: #a96a00;
}

.lead-follow-up-panel__action--cancelled {
  color: #75817f;
}

.lead-follow-up-panel__action-heading strong {
  font-size: 11px;
  line-height: 16px;
}

.lead-follow-up-panel__delivery-statuses {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 5px;
}

.lead-follow-up-panel__delivery-statuses > span {
  white-space: nowrap;
}

.lead-follow-up-panel__action .lead-follow-up-panel__action-description {
  width: fit-content;
  max-width: 100%;
  margin-top: 3px;
  padding: 4px 8px;
  border: 1px solid #e6e0d4;
  border-radius: 4px;
  background: #f7f4ed;
  color: #5f6f6d;
  font-weight: 500;
}

.lead-follow-up-panel__preview-button {
  display: flex;
  justify-content: flex-end;
  min-width: 0;
  min-height: 0;
  float: right;
  margin-top: -17px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #4f8b25;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
}

.lead-follow-up-panel__template {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 3px 8px;
  margin-top: 9px;
  padding: 10px;
  border: 1px solid #d9e3d3;
  border-radius: 5px;
  background: #f9fcf7;
}

.lead-follow-up-panel__template strong,
.lead-follow-up-panel__template span {
  font-size: 10px;
}

.lead-follow-up-panel__template span {
  color: #87918f;
}

.lead-follow-up-panel__template p {
  grid-column: 1 / -1;
  color: #536361;
  white-space: pre-wrap;
}

.lead-follow-up-panel__warning,
.lead-follow-up-panel__error {
  margin: 0;
  padding: 9px 10px;
  border: 1px solid #ead39b;
  border-radius: 5px;
  background: #fff9e9;
  color: #7d642e;
  font-size: 10px;
}

.lead-follow-up-panel__error button {
  display: inline-flex;
  min-width: 0;
  min-height: 0;
  margin-left: 5px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font-weight: 700;
  cursor: pointer;
}

.lead-follow-up-panel__skeleton {
  display: grid;
  gap: 9px;
}

.lead-follow-up-panel__skeleton span {
  height: 15px;
  border-radius: 5px;
  background: #edf0eb;
}

.lead-follow-up-panel__skeleton span:nth-child(2) {
  width: 70%;
}

.lead-follow-up-panel__skeleton span:nth-child(n+3) {
  height: 54px;
}

@media (max-width: 600px) {
  .lead-follow-up-panel__header,
  .lead-follow-up-panel__empty {
    align-items: flex-start;
    flex-direction: column;
  }

  .lead-follow-up-panel__progress-row {
    grid-template-columns: 1fr;
  }
}
</style>
